import {
  MinusIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useCart } from '../contexts/CartContext';
import { useLocale } from '../contexts/LocaleContext';
import { productService } from '../services/productService';
import type {
  MaterialOption,
  ProcessingExtra,
  SelectedExtra,
} from '../types/product';

const Cart: React.FC = () => {
  const pageTitle = 'Shopping Cart';
  const pageDescription = 'Review items in your cart before checkout.';
  const {
    cart,
    isLoading,
    error,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartItemCount,
    getCartTotal,
  } = useCart();

  const { formatCurrency, convertPrice } = useLocale();

  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingMaterialId, setEditingMaterialId] = useState<string>('');
  const [editingExtraIds, setEditingExtraIds] = useState<Set<string>>(
    new Set()
  );
  const [editingSpecialInstructions, setEditingSpecialInstructions] =
    useState('');

  const handleQuantityChange = async (
    productId: string,
    newQuantity: number
  ) => {
    if (newQuantity < 1) {
      await removeFromCart(productId);
    } else {
      // Find the current item to preserve its material and extras
      const currentItem = cart?.items.find(
        item => item.productId === productId
      );
      await updateCartItem(
        productId,
        newQuantity,
        currentItem?.selectedMaterial?.id,
        currentItem?.selectedExtras?.map((extra: SelectedExtra) => extra.id) ||
          [],
        currentItem?.specialInstructions
      );
    }
  };

  const handleRemoveItem = async (productId: string) => {
    await removeFromCart(productId);
  };

  const handleClearCart = async () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      await clearCart();
    }
  };

  const handleEditItem = (item: any) => {
    setEditingItemId(item.productId);
    setEditingMaterialId(item.selectedMaterial?.id || '');
    setEditingExtraIds(
      new Set(item.selectedExtras?.map((e: SelectedExtra) => e.id) || [])
    );
    setEditingSpecialInstructions(item.specialInstructions || '');
  };

  const handleSaveEdit = async (productId: string) => {
    const currentItem = cart?.items.find(item => item.productId === productId);
    if (currentItem) {
      await updateCartItem(
        productId,
        currentItem.quantity,
        editingMaterialId,
        Array.from(editingExtraIds),
        editingSpecialInstructions
      );
    }
    setEditingItemId(null);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
  };

  const handleExtraChange = (extraId: string, checked: boolean) => {
    const newSelectedExtras = new Set(editingExtraIds);
    if (checked) {
      newSelectedExtras.add(extraId);
    } else {
      newSelectedExtras.delete(extraId);
    }
    setEditingExtraIds(newSelectedExtras);
  };

  // Component to display delivery estimate for each cart item
  const CartItemDeliveryEstimate: React.FC<{ item: any }> = ({ item }) => {
    const { data: deliveryEstimate } = useQuery({
      queryKey: [
        'delivery-estimate',
        item.productId,
        item.selectedMaterial?.id,
        item.selectedExtras?.map((e: SelectedExtra) => e.id),
        item.quantity,
      ],
      queryFn: () =>
        productService.getProductDeliveryEstimate(
          item.productId,
          item.selectedMaterial?.id,
          item.selectedExtras?.map((e: SelectedExtra) => e.id) || [],
          item.quantity
        ),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

    if (!deliveryEstimate) return null;

    return (
      <div className='mt-2 p-2 bg-gray-50 rounded-md'>
        <div className='text-sm text-gray-600'>
          <span className='font-medium'>Delivery: </span>
          <span
            className={
              deliveryEstimate.isInStock ? 'text-green-600' : 'text-orange-600'
            }
          >
            {deliveryEstimate.deliveryEstimateDisplay}
          </span>
        </div>
        {deliveryEstimate.stockStatus && (
          <div className='text-xs text-gray-500 mt-1'>
            Status: {deliveryEstimate.stockStatus}
          </div>
        )}
      </div>
    );
  };

  // Component to display editing form for cart item
  const CartItemEditForm: React.FC<{ item: any }> = ({ item }) => {
    const { data: product } = useQuery({
      queryKey: ['product', item.productId],
      queryFn: () => productService.getProduct(item.productId),
      enabled: editingItemId === item.productId,
    });

    if (!product) return null;

    return (
      <div className='mt-4 p-4 bg-gray-50 rounded-md'>
        <h4 className='font-medium text-gray-900 mb-3'>Edit Item Options</h4>

        {/* Material Selection */}
        {product.materialOptions?.length > 0 && (
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Material:
            </label>
            <select
              value={editingMaterialId}
              onChange={e => setEditingMaterialId(e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500'
              aria-label='Select material'
            >
              <option value=''>Select Material</option>
              {product.materialOptions
                .filter((material: MaterialOption) => material.isAvailable)
                .map((material: MaterialOption) => (
                  <option key={material.id} value={material.id}>
                    {material.displayName}
                    {material.priceModifier !== 0 &&
                      ` (${
                        material.priceModifier > 0 ? '+' : ''
                      }${formatAmount(material.priceModifier)})`}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Processing Extras */}
        {product.processingExtras?.length > 0 && (
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Processing Extras:
            </label>
            <div className='space-y-2'>
              {product.processingExtras
                .filter((extra: ProcessingExtra) => extra.isAvailable)
                .map((extra: ProcessingExtra) => (
                  <div
                    key={extra.id}
                    className='flex items-center justify-between p-2 border rounded'
                  >
                    <div className='flex items-center'>
                      <input
                        type='checkbox'
                        id={`extra-${extra.id}`}
                        checked={editingExtraIds.has(extra.id)}
                        onChange={e =>
                          handleExtraChange(extra.id, e.target.checked)
                        }
                        className='mr-2'
                      />
                      <label htmlFor={`extra-${extra.id}`} className='text-sm'>
                        <div className='font-medium'>{extra.displayName}</div>
                        <div className='text-gray-500'>{extra.description}</div>
                      </label>
                    </div>
                    <div className='text-sm font-medium text-blue-600'>
                      +{formatAmount(extra.price)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Special Instructions */}
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Special Instructions:
          </label>
          <textarea
            value={editingSpecialInstructions}
            onChange={e => setEditingSpecialInstructions(e.target.value)}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500'
            placeholder='Any special requests or modifications...'
          />
        </div>

        {/* Action Buttons */}
        <div className='flex space-x-2'>
          <button
            onClick={() => handleSaveEdit(item.productId)}
            className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm'
          >
            Save Changes
          </button>
          <button
            onClick={handleCancelEdit}
            className='px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm'
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        <SEOHead title={pageTitle} description={pageDescription} />
        <div className='container mx-auto px-4 py-8'>
          <div className='flex justify-center items-center h-64'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600' />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEOHead title={pageTitle} description={pageDescription} />
        <div className='container mx-auto px-4 py-8'>
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded'>
            <p>Error loading cart: {error}</p>
          </div>
        </div>
      </>
    );
  }

  const isEmpty = !cart || !cart.items || cart.items.length === 0;

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='container mx-auto px-4 py-8'>
        <div className='flex justify-between items-center mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>Shopping Cart</h1>
          {!isEmpty && (
            <button
              onClick={handleClearCart}
              className='text-red-600 hover:text-red-800 font-medium'
            >
              Clear Cart
            </button>
          )}
        </div>

        {isEmpty ? (
          <div className='text-center py-16'>
            <div className='mb-4'>
              <svg
                className='mx-auto h-24 w-24 text-gray-400'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1}
                  d='M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6M17 13v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01'
                />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-2'>
              Your cart is empty
            </h2>
            <p className='text-gray-600 mb-6'>
              Start shopping to add items to your cart
            </p>
            <Link
              to='/search'
              className='inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700'
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            {/* Cart Items */}
            <div className='lg:col-span-2'>
              <div className='bg-white shadow rounded-lg overflow-hidden'>
                <div className='px-6 py-4 border-b border-gray-200'>
                  <h2 className='text-lg font-medium text-gray-900'>
                    Cart Items ({getCartItemCount()})
                  </h2>
                </div>
                <div className='divide-y divide-gray-200'>
                  {cart?.items?.map(item => (
                    <div key={item.productId} className='p-6'>
                      <div className='flex items-center'>
                        {/* Product Image */}
                        <div className='flex-shrink-0 w-20 h-20'>
                          <img
                            className='w-20 h-20 rounded-md object-cover'
                            src={
                              item.productImageUrl || '/placeholder-image.jpg'
                            }
                            alt={item.productName}
                            onError={e => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-image.jpg';
                            }}
                          />
                        </div>

                        {/* Product Details */}
                        <div className='ml-6 flex-1'>
                          <div className='flex justify-between'>
                            <div>
                              <h3 className='text-lg font-medium text-gray-900'>
                                <Link
                                  to={`/products/${item.productId}`}
                                  className='hover:text-blue-600'
                                >
                                  {item.productName}
                                </Link>
                              </h3>
                              {item.scale && (
                                <p className='text-sm text-gray-500'>
                                  Scale: {item.scale}
                                </p>
                              )}

                              {/* Material Information */}
                              {item.selectedMaterial && (
                                <p className='text-sm text-gray-600'>
                                  Material: {item.selectedMaterial.displayName}
                                </p>
                              )}

                              {/* Extras Information */}
                              {item.selectedExtras &&
                                item.selectedExtras.length > 0 && (
                                  <p className='text-sm text-gray-600'>
                                    Extras:{' '}
                                    {item.selectedExtras
                                      .map(
                                        (extra: SelectedExtra) =>
                                          extra.displayName
                                      )
                                      .join(', ')}
                                  </p>
                                )}

                              {/* Special Instructions */}
                              {item.specialInstructions && (
                                <p className='text-sm text-gray-600'>
                                  Notes: {item.specialInstructions}
                                </p>
                              )}

                              {/* Pricing Breakdown */}
                              <div className='mt-2'>
                                <p className='text-lg font-semibold text-gray-900'>
                                  {formatAmount(item.unitPrice)} each
                                </p>

                                {/* Show pricing breakdown if item has customizations */}
                                {(item.selectedMaterial ||
                                  (item.selectedExtras &&
                                    item.selectedExtras.length > 0)) && (
                                  <div className='text-sm text-gray-500 space-y-1'>
                                    <div>
                                      Base: {formatAmount(item.basePrice)}
                                    </div>
                                    {item.selectedMaterial &&
                                      item.selectedMaterial.priceModifier !==
                                        0 && (
                                        <div>
                                          Material:{' '}
                                          {item.selectedMaterial.priceModifier >
                                          0
                                            ? '+'
                                            : ''}
                                          {formatAmount(
                                            item.selectedMaterial.priceModifier
                                          )}
                                        </div>
                                      )}
                                    {item.selectedExtras &&
                                      item.selectedExtras.length > 0 && (
                                        <div>
                                          Extras: +
                                          {formatAmount(
                                            item.selectedExtras.reduce(
                                              (sum, extra) => sum + extra.price,
                                              0
                                            )
                                          )}
                                        </div>
                                      )}
                                  </div>
                                )}
                              </div>

                              {/* Delivery Estimate */}
                              <CartItemDeliveryEstimate item={item} />
                            </div>
                            <div className='flex space-x-2'>
                              <button
                                onClick={() => handleEditItem(item)}
                                className='text-blue-600 hover:text-blue-800'
                                aria-label='Edit item options'
                              >
                                <PencilIcon className='h-5 w-5' />
                              </button>
                              <button
                                onClick={() => handleRemoveItem(item.productId)}
                                className='text-red-600 hover:text-red-800'
                                aria-label='Remove item from cart'
                              >
                                <TrashIcon className='h-5 w-5' />
                              </button>
                            </div>
                          </div>

                          {/* Edit Form */}
                          {editingItemId === item.productId && (
                            <CartItemEditForm item={item} />
                          )}

                          {/* Quantity Controls */}
                          <div className='mt-4 flex items-center'>
                            <div className='flex items-center border border-gray-300 rounded-md'>
                              <button
                                onClick={() =>
                                  handleQuantityChange(
                                    item.productId,
                                    item.quantity - 1
                                  )
                                }
                                className='p-2 hover:bg-gray-100'
                                disabled={isLoading}
                                aria-label='Decrease quantity'
                              >
                                <MinusIcon className='h-4 w-4' />
                              </button>
                              <span className='px-4 py-2 text-center min-w-[3rem]'>
                                {item.quantity}
                              </span>
                              <button
                                onClick={() =>
                                  handleQuantityChange(
                                    item.productId,
                                    item.quantity + 1
                                  )
                                }
                                className='p-2 hover:bg-gray-100'
                                disabled={isLoading}
                                aria-label='Increase quantity'
                              >
                                <PlusIcon className='h-4 w-4' />
                              </button>
                            </div>
                            <div className='ml-6'>
                              <p className='text-lg font-semibold text-gray-900'>
                                {formatAmount(item.totalPrice)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className='lg:col-span-1'>
              <div className='bg-white shadow rounded-lg p-6 sticky top-4'>
                <h2 className='text-lg font-medium text-gray-900 mb-4'>
                  Order Summary
                </h2>

                <div className='space-y-3'>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>Subtotal</span>
                    <span className='font-medium'>
                      {formatAmount(getCartTotal())}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>Shipping</span>
                    <span className='font-medium'>Calculated at checkout</span>
                  </div>
                  <div className='border-t border-gray-200 pt-3'>
                    <div className='flex justify-between'>
                      <span className='text-lg font-semibold'>Total</span>
                      <span className='text-lg font-semibold'>
                        {formatAmount(getCartTotal())}
                      </span>
                    </div>
                  </div>
                </div>

                <Link to='/checkout'>
                  <button
                    className='w-full mt-6 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 font-medium disabled:opacity-50'
                    disabled={
                      isLoading || !cart?.items || cart.items.length === 0
                    }
                  >
                    Proceed to Checkout
                  </button>
                </Link>

                <Link
                  to='/search'
                  className='block w-full mt-3 text-center text-blue-600 hover:text-blue-800 font-medium'
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;
